import React from 'react';

/**
 * Les icônes de la maquette sont des SVG inline recopiés d'une page à l'autre.
 * On les centralise ici, verbatim (mêmes viewBox, mêmes tracés, mêmes épaisseurs)
 * pour que le rendu reste ISO tout en n'ayant qu'un seul endroit à corriger.
 *
 * Toutes acceptent `size` et se déclarent décoratives par défaut :
 * `aria-hidden`, la valeur accessible venant du texte qui les accompagne.
 */
const svgProps = (size, props) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  'aria-hidden': 'true',
  focusable: 'false',
  ...props,
});

const strokeProps = (width = 2.5) => ({
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: width,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

export const PinIcon = ({ size = 15, strokeWidth = 2.5, ...rest }) => (
  <svg {...svgProps(size, rest)} {...strokeProps(strokeWidth)}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

export const SearchIcon = ({ size = 13, strokeWidth = 3, ...rest }) => (
  <svg {...svgProps(size, rest)} {...strokeProps(strokeWidth)}>
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

export const ClockIcon = ({ size = 15, ...rest }) => (
  <svg {...svgProps(size, rest)} {...strokeProps()}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </svg>
);

export const PhoneIcon = ({ size = 15, ...rest }) => (
  <svg {...svgProps(size, rest)} {...strokeProps()}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

/**
 * `scale` resserre le polygone sur lui-même (centré sur le centre visuel de
 * l'étoile, 12/11.5) : à trait égal, le contour rond aux pointes déborde
 * sinon visiblement au-delà du polygone plein de `StarSolidIcon`, qui lui
 * n'a pas de trait. Sans effet quand `scale` vaut 1 (défaut historique).
 */
export const StarOutlineIcon = ({ size = 15, stroke = 'currentColor', strokeWidth = 2.2, scale = 1, ...rest }) => (
  <svg {...svgProps(size, rest)} {...strokeProps(strokeWidth)} stroke={stroke}>
    <polygon
      points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
      transform={scale !== 1 ? `translate(12 11.5) scale(${scale}) translate(-12 -11.5)` : undefined}
    />
  </svg>
);

/** Étoile pleine du widget Goodays — remplie en jaune, pas en `currentColor`. */
export const StarSolidIcon = ({ size = 26, fill = 'var(--jaune)', ...rest }) => (
  <svg {...svgProps(size, rest)} fill={fill} xmlns="http://www.w3.org/2000/svg">
    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
  </svg>
);

export const FacebookIcon = ({ size = 16, ...rest }) => (
  <svg {...svgProps(size, rest)} fill="currentColor">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

export const InstagramIcon = ({ size = 16, ...rest }) => (
  <svg
    {...svgProps(size, rest)}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

/** Variante du footer : cercle + point plein, tracé différent de celle du panneau. */
export const InstagramFooterIcon = ({ size = 16, ...rest }) => (
  <svg {...svgProps(size, rest)} fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="2" width="20" height="20" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

export const YoutubeIcon = ({ size = 16, ...rest }) => (
  <svg {...svgProps(size, rest)} fill="currentColor">
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z" />
  </svg>
);

export const TiktokIcon = ({ size = 16, ...rest }) => (
  <svg {...svgProps(size, rest)} fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z" />
  </svg>
);
